import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Form, Input, InputNumber, notification, Select } from 'antd';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import ImagePicker from 'components/ImagePicker';

import {
  saveCamapignGiftStart,
  setCampaignState,
  fetchCampaignGiftsStart,
  addCampaignGift,
  editCampaignGift,
  fetchCampaignGiftOperators,
} from 'redux/campaign/actions';

import { hideModal } from 'redux/modal/actions';

import ModalTypes from 'constants/modalTypes';
import ClientType from 'constants/clientType';

import { intl } from 'utils/intl';

import { uniqueId } from 'utils/uniqueId';

const { TextArea } = Input;
const { Option } = Select;

const formLayout = {
  labelCol: { span: 7 },
  wrapperCol: { span: 18 },
};

const GiftDetails = ({ currentModals, gift, campaignId }) => {
  const dispatch = useDispatch();

  const {
    isSavingCampaignGift,
    didSaveCampaignGift,
    campaignDetailsGifts,
    campaignGiftOperators,
    isFetchingCampaignGiftOperators,
    hasFetchedCampaignGiftOperators,
  } = useSelector(state => state.campaign);

  const [form] = Form.useForm();
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);

  useEffect(() => {
    if (didSaveCampaignGift) {
      dispatch(setCampaignState({ didSaveCampaignGift: false }));
      dispatch(fetchCampaignGiftsStart(decodeURI(campaignId)));
      dispatch(hideModal(ModalTypes.GIFT_DETAILS));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [didSaveCampaignGift]);

  useEffect(() => {
    if (gift) {
      const { title, description, magicNumber, giftAvailableQuantity, image } = gift;
      setFilePreview(image);
      form.setFieldsValue({ title, description, magicNumber, giftAvailableQuantity, image });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gift]);

  useEffect(() => {
    if (process.env.REACT_APP_CLIENT === ClientType.BH) {
      dispatch(fetchCampaignGiftOperators());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasFetchedCampaignGiftOperators && gift) {
      if (gift.mobileOperatorId && gift.mobileOperatorId !== '') {
        form.setFieldsValue({ mobileOperatorId: gift.mobileOperatorId });
      }
      dispatch(setCampaignState({ hasFetchedCampaignGiftOperators: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFetchedCampaignGiftOperators]);

  const handleOnSave = () => {
    const formValues = form.getFieldsValue([
      'title',
      'description',
      'magicNumber',
      'giftAvailableQuantity',
    ]);

    const { validFields, formData } = areFieldsValid(form.getFieldsValue());

    if (!validFields) {
      form.setFields(formData);
    } else {
      const tempGift = gift
        ? {
            ...gift,
            ...formValues,
            giftInitialQuantity: formValues.giftAvailableQuantity,
          }
        : {
            ...formValues,
            giftInitialQuantity: formValues.giftAvailableQuantity,
            isActive: true,
            image: gift && campaignId && selectedFile ? `gifts/${selectedFile.name}` : filePreview,
            totalCouponScanRequests: 0,
            giftType: {
              id: 'giftTypeIdDefault',
            },
            mobileOperator: {
              id: 'mobileOperatorNone',
            },
          };

      if ((gift && gift.campaign.id) || campaignId) {
        dispatch(
          saveCamapignGiftStart(
            { campaign: { id: decodeURI(campaignId) }, ...tempGift },
            selectedFile,
          ),
        );
      } else if (gift) {
        if (!isMagicNumberAvailable(false, tempGift)) {
          notification.error({
            message: intl.formatMessage({ id: 'campaign.notification.error.title' }),
            description: intl.formatMessage({ id: 'campaign.notification.magicNumber.error' }),
          });
          return;
        }

        dispatch(
          editCampaignGift({
            ...tempGift,
            isActive: true,
            image: selectedFile ? filePreview : gift.image,
            imageFileName: (selectedFile && `gifts/${selectedFile.name}`) || gift.imageFileName,
          }),
        );
        dispatch(hideModal(ModalTypes.GIFT_DETAILS));
      } else {
        if (!isMagicNumberAvailable(false, tempGift)) {
          notification.error({
            message: intl.formatMessage({ id: 'campaign.notification.error.title' }),
            description: intl.formatMessage({ id: 'campaign.notification.magicNumber.error' }),
          });
          return;
        }

        dispatch(
          addCampaignGift({
            tempId: uniqueId(),
            ...tempGift,
            imageFileName: (selectedFile && `gifts/${selectedFile.name}`) || '',
          }),
        );
        dispatch(hideModal(ModalTypes.GIFT_DETAILS));
      }
    }
  };

  const isMagicNumberAvailable = (isAddingNewGift, editedGift) => {
    let isAvailable = true;

    campaignDetailsGifts.forEach(item => {
      if (isAddingNewGift && item.magicNumber === editedGift) {
        isAvailable = false;
      } else if (
        !isAddingNewGift &&
        editedGift.tempId !== item.tempId &&
        item.magicNumber === editedGift.magicNumber
      ) {
        isAvailable = false;
      }
    });

    return isAvailable;
  };

  const areFieldsValid = values => {
    const { title, magicNumber, giftAvailableQuantity } = values;

    let formData = [];

    const onlyNumbersRegEx = /^[0-9]+$/;

    if (!title || title === '') {
      formData = [
        ...formData,
        {
          name: 'title',
          errors: [intl.formatMessage({ id: 'campaign.gift.form.validation.title.required' })],
        },
      ];
    }

    if (!magicNumber || magicNumber === '') {
      formData = [
        ...formData,
        {
          name: 'magicNumber',
          errors: [
            intl.formatMessage({ id: 'campaign.gift.form.validation.magicNumber.required' }),
          ],
        },
      ];
    } else if (!String(magicNumber).match(onlyNumbersRegEx)) {
      formData = [
        ...formData,
        {
          name: 'magicNumber',
          errors: [
            intl.formatMessage({ id: 'campaign.gift.form.validation.magicNumber.onlyNumbers' }),
          ],
        },
      ];
    }

    if (!giftAvailableQuantity || giftAvailableQuantity === '') {
      formData = [
        ...formData,
        {
          name: 'giftAvailableQuantity',
          errors: [intl.formatMessage({ id: 'campaign.gift.form.validation.totalGifts.required' })],
        },
      ];
    } else if (!String(giftAvailableQuantity).match(onlyNumbersRegEx)) {
      formData = [
        ...formData,
        {
          name: 'giftAvailableQuantity',
          errors: [
            intl.formatMessage({ id: 'campaign.gift.form.validation.totalGifts.onlyNumbers' }),
          ],
        },
      ];
    }

    return { validFields: formData.length === 0, formData };
  };

  const handleToggle = () => {
    dispatch(hideModal(ModalTypes.GIFT_DETAILS));
  };

  return (
    <Modal
      isOpen={Object.prototype.hasOwnProperty.call(currentModals, ModalTypes.GIFT_DETAILS)}
      toggle={handleToggle}
      centered
      scrollable
      unmountOnClose
    >
      <ModalHeader toggle={handleToggle}>
        {gift
          ? intl.formatMessage({ id: 'campaign.gift.details.title' })
          : intl.formatMessage({ id: 'campaign.gift.new.title' })}
      </ModalHeader>
      <ModalBody>
        <Form
          form={form}
          preserve={false}
          initialValues={
            process.env.REACT_APP_CLIENT === ClientType.BH ? { mobileOperatorId: 'none' } : {}
          }
          {...formLayout}
        >
          <Form.Item
            name='productPhoto'
            label={intl.formatMessage({ id: 'campaign.gift.form.item.image.label' })}
            labelAlign='left'
            wrapperCol={{ span: 9 }}
          >
            <ImagePicker
              image={selectedFile}
              setImage={setSelectedFile}
              preview={filePreview}
              setPreview={setFilePreview}
              shouldCompressFile
            />
          </Form.Item>

          <Form.Item
            name='title'
            label={intl.formatMessage({ id: 'campaign.gift.form.item.name.label' })}
            labelAlign='left'
            rules={[
              {
                required: true,
                message: intl.formatMessage({ id: 'campaign.gift.form.validation.title.required' }),
              },
            ]}
          >
            <Input
              placeholder={intl.formatMessage({ id: 'campaign.gift.form.item.name.placeholder' })}
            />
          </Form.Item>

          <Form.Item
            name='description'
            label={intl.formatMessage({ id: 'campaign.gift.form.item.description.label' })}
            labelAlign='left'
          >
            <TextArea
              rows='4'
              value='test'
              style={{ resize: 'none' }}
              placeholder={intl.formatMessage({
                id: 'campaign.gift.form.item.description.placeholder',
              })}
            />
          </Form.Item>

          <Form.Item
            name='magicNumber'
            label={intl.formatMessage({ id: 'campaign.gift.form.item.magicNumber.label' })}
            labelAlign='left'
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'campaign.gift.form.validation.magicNumber.required',
                }),
              },
              {
                type: 'number',
                message: intl.formatMessage({
                  id: 'campaign.gift.form.validation.magicNumber.onlyNumbers',
                }),
              },
            ]}
          >
            <InputNumber
              placeholder={intl.formatMessage({
                id: 'campaign.gift.form.item.magicNumber.placeholder',
              })}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name='giftAvailableQuantity'
            label={intl.formatMessage({ id: 'campaign.gift.form.item.totalGifts.label' })}
            labelAlign='left'
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'campaign.gift.form.validation.totalGifts.required',
                }),
              },
              {
                type: 'number',
                message: intl.formatMessage({
                  id: 'campaign.gift.form.validation.totalGifts.onlyNumbers',
                }),
              },
            ]}
          >
            <InputNumber
              placeholder={intl.formatMessage({
                id: 'campaign.gift.form.item.totalGifts.placeholder',
              })}
              style={{ width: '100%' }}
            />
          </Form.Item>

          {process.env.REACT_APP_CLIENT === ClientType.BH && (
            <Form.Item name='mobileOperatorId' label='Operator' labelAlign='left'>
              <Select
                placeholder='Select operator'
                disabled={isFetchingCampaignGiftOperators}
                loading={isFetchingCampaignGiftOperators}
              >
                <Option value='none'>N/A</Option>
                {campaignGiftOperators.map(operator => (
                  <Option key={operator.id} value={operator.id}>
                    {operator.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button style={{ width: '7rem' }} key='cancel' onClick={handleToggle}>
          {intl.formatMessage({ id: 'campaign.gift.button.cancel' })}
        </Button>
        <Button
          className='btn btn-primary'
          key='save'
          style={{ width: '7rem' }}
          onClick={handleOnSave}
          loading={isSavingCampaignGift}
        >
          {intl.formatMessage({ id: 'campaign.gift.button.save' })}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default GiftDetails;
